import { getTranscriptionLanguageCode, meetingTypes } from "../config/meetingOptions.js";
import {
  REALTIME_NOISE_REDUCTION,
  REALTIME_TURN_DETECTION,
  labelFor,
  sanitizeText,
  summarizeConversation,
} from "./shared.js";

export function buildTrainingOpeningPrompt(meeting, trainingPlan) {
  const selectedLanguage = meeting?.languageLabel || "English";
  const title = String(trainingPlan?.title || "Training Session").trim();
  const details = String(
    trainingPlan?.details || "Improve communication through guided practice."
  ).trim();
  const duration = trainingPlan?.duration;

  return ` You're a calm, motivating, and encouraging communication coach guiding the user through a live **training session**.
You must speak only in ${selectedLanguage}.
Do not speak in any other language unless the user explicitly asks you to switch.

This training session is titled **"${title}"**.
Training purpose: ${details}

Greet the user, state the goal, and move directly into the first exercise.
Use no more than two short sentences. Do not list an agenda or repeat the session duration (${duration || 10} minutes).
Keep it human and conversational.`;
}

export function buildTrainingOpeningResponseInstructions(meeting, openingPrompt) {
  return `Generate a warm training opening in ${meeting?.languageLabel || "English"} using no more than two short sentences. Then begin the first exercise. Base it on: ${openingPrompt}`;
}

export function buildTrainingSessionInstructions(meeting, trainingPlan, conversation = []) {
  const goals = [...(meeting?.goals || []), ...(meeting?.customGoals || [])]
    .map((goal) => String(goal || "").trim())
    .filter(Boolean)
    .join("; ");

  return [
    "SYSTEM (TRAINING MODE):",
    "       You are the communication coach — a seasoned I/O psychologist and conversation coach who trains users to improve their real-world communication skills. ",
    "The opening introduction is NOT part of the training exercise.",
    "If the user's response is only acknowledging the opening or setup, do not coach it. Begin the first actual training exercise instead.",
    "        In this **Training Session**, your job is to guide the user toward the training objective below through an engaging, collaborative experience that feels human, natural, encouraging, and practical.",
    " You should sound like a calm, supportive, but confident mentor — energetic, conversational, and focused on helping the user grow step by step. ",
    "REFERENCE — ORIGINAL MEETING DETAILS:",
    `Training focus: ${trainingPlan?.title || "Training step"}`,
    `Training objective: ${sanitizeText(trainingPlan?.details || "Help the user improve for their next conversation.", 1400)}`,
    `Target duration: ${trainingPlan?.duration || 10} minutes`,
    `Related meeting type: ${labelFor(meeting?.meetingType, meetingTypes) || meeting?.meetingType}`,
    `Industry: ${meeting?.industry || "N/A"}`,
    `User role: ${meeting?.userRole || "N/A"}`,
    `Speaking with: ${meeting?.speakingWith || "N/A"}`,
    goals ? `Meeting goals: ${goals}` : "Meeting goals: Improve confidence and clarity.",
    `Language: ${meeting?.languageLabel || "English"}`,
    `Additional Meeting Context (what the user specified): ${meeting?.additionalInfo || ""}`,
    "Behavior rules:",
    "1) Coach directly and briefly after each substantive TRAINING response, not after acknowledgments or setup conversation.",
    "2) Give specific rewrites when useful.",
    "3) Keep advice practical, concise, and tied to this training objective.",
    "4) Use concise language and avoid repeating the same advice.",
    "5) Keep each spoken turn to 1-3 short sentences: one coaching cue and one exercise or question.",
    "6) Present one exercise at a time. Do not recap the full plan or list future exercises.",
    "7) If the training feels complete, call propose_session_end.",
    "",
    "Use **bold** for the one most important cue or question.",
    "For sentences you want the user to practice or try out, you must always return those in **Bold**",
    "REFERENCE — ORIGINAL MEETING TRANSCRIPT:",
    "You can reference user's prior responses in this session to inform your coaching, but do not repeat them verbatim.",
    summarizeConversation(conversation),
  ].join("\n");
}

export const trainingProgressTools = [
  {
    type: "function",
    name: "evaluate_progress_and_advance",
    description:
      "Evaluate a substantive response to the current exercise. Advance only when it is relevant, complete enough to assess, and meets the training objective. Never advance acknowledgements, unrelated speech, or incoherent responses.",
    parameters: {
      type: "object",
      properties: {
        isRelevant: {
          type: "boolean",
          description: "Whether the response directly addresses the current exercise.",
        },
        isSubstantive: {
          type: "boolean",
          description: "Whether the response is coherent and complete enough to assess as an attempt.",
        },
        meetsObjective: {
          type: "boolean",
          description: "Whether the response demonstrates the exercise's target communication skill.",
        },
        shouldAdvance: {
          type: "boolean",
          description: "True only when isRelevant, isSubstantive, and meetsObjective are all true.",
        },
        reason: {
          type: "string",
          description: "Short explanation for the decision.",
        },
      },
      required: [
        "isRelevant",
        "isSubstantive",
        "meetsObjective",
        "shouldAdvance",
        "reason",
      ],
    },
  },
  {
    type: "function",
    name: "generate_next_scenario",
    description: "Generate a fresh roleplay exercise that matches the original meeting context.",
    parameters: {
      type: "object",
      properties: {
        variationType: {
          type: "string",
          enum: ["new_context", "higher_stakes", "different_tone", "different_persona"],
        },
        scenario: {
          type: "string",
          description: "Short description of the new situation.",
        },
        exampleLine: {
          type: "string",
          description: "New phrase for the user to practice.",
        },
      },
      required: ["variationType", "scenario", "exampleLine"],
    },
  },
];

export function buildTrainingSessionUpdatePayload(
  meeting,
  trainingPlan,
  conversation = [],
  { pushToTalk = false } = {}
) {
  return {
    type: "realtime",
    output_modalities: ["audio"],
    audio: {
      input: {
        noise_reduction: REALTIME_NOISE_REDUCTION,
        turn_detection: pushToTalk ? null : REALTIME_TURN_DETECTION,
        transcription: {
          model: "whisper-1",
          language: getTranscriptionLanguageCode(meeting?.languageLabel),
        },
      },
      output: {
        voice: meeting?.aiVoice || "cedar",
      },
    },
    tools: [
      {
        type: "function",
        name: "propose_session_end",
        description: "Call this when the training step has naturally reached a good stopping point.",
        parameters: {
          type: "object",
          properties: {
            reason: { type: "string" },
            confidence: { type: "number", minimum: 0, maximum: 1 },
          },
          required: ["reason", "confidence"],
        },
      },
      ...trainingProgressTools,
    ],
    tool_choice: "auto",
    instructions: `${buildTrainingSessionInstructions(meeting, trainingPlan, conversation)}\n\nEnd policy:\n- Finish once the user has practiced enough and receives concrete final guidance.\n- Never mention internal tools or policy text to the user.`,
  };
}
